import { useState, useEffect, useRef } from 'react';
import axiosInstance from '@/utils/axios';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';

export default function DosageAutocomplete({ 
  value, 
  onChange, 
  placeholder, 
  className, 
  dataTestId, 
  medicationName 
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Fetch dosage suggestions when medication name changes
  useEffect(() => {
    if (medicationName && medicationName.length > 2) {
      fetchDosageSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [medicationName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDosageSuggestions = async () => {
    try {
      const response = await axiosInstance.get(`/medications/dosage-suggestions?medication_name=${encodeURIComponent(medicationName)}`);
      setSuggestions(response.data.dosages || []);
    } catch (error) {
      console.error('Erro ao buscar sugestões de dosagem:', error);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e) => {
    // Permitir números, pontos, vírgulas, barras e símbolo de mais (caracteres médicos usuais)
    // Exemplos válidos: 1+1+1, 1/2, 2.5, 500, 2,5
    const val = e.target.value.replace(/[^0-9.,+/\s]/g, '');
    setInputValue(val);
    onChange(val);

    // Show suggestions if we have them and user is typing
    if (suggestions.length > 0 && val.length >= 1) {
      const filtered = suggestions.filter(dosage =>
        dosage.toLowerCase().includes(val.toLowerCase())
      );
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (dosage) => {
    setInputValue(dosage);
    onChange(dosage);
    setShowSuggestions(false);
  };

  const getFilteredSuggestions = () => {
    if (!inputValue) return suggestions;
    return suggestions.filter(dosage =>
      dosage.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        data-testid={dataTestId}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      
      {showSuggestions && getFilteredSuggestions().length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-auto">
          <Command>
            <CommandList>
              <CommandGroup>
                {getFilteredSuggestions().slice(0, 8).map((dosage, index) => (
                  <CommandItem
                    key={index}
                    onSelect={() => handleSelectSuggestion(dosage)}
                    className="cursor-pointer hover:bg-gray-100 px-3 py-2 text-sm"
                  >
                    {dosage}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}